import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiUrl } from '../utils/api';

const API_URL = apiUrl('/api/emails-autorizados/');

const INITIAL_FORM = {
  email: '',
  jerarquia: 'tecnico',
  puede_ver_dashboard: false,
  puede_administrar_permisos: false,
  puede_eliminar_registros: false,
  puede_ser_medico_responsable: false,
  puede_ser_operario_inta: false,
  activo: true,
};

const PermisosPage = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const token = localStorage.getItem('token');

  const headers = useMemo(
    () => ({
      Authorization: `Token ${token}`,
    }),
    [token]
  );

  const loadRegistros = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API_URL, { headers });
      setRegistros(response.data);
    } catch (fetchError) {
      console.error(fetchError);
      setError('No se pudieron cargar los permisos autorizados.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadRegistros();
  }, [loadRegistros]);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditingId(null);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      ...form,
      email: form.email.trim().toLowerCase(),
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}${editingId}/`, payload, { headers });
        toast.success('Permiso actualizado correctamente');
      } else {
        await axios.post(API_URL, payload, { headers });
        toast.success('Permiso creado correctamente');
      }

      resetForm();
      loadRegistros();
    } catch (saveError) {
      console.error(saveError);
      toast.error('No se pudo guardar el permiso');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (registro) => {
    setEditingId(registro.id);
    setForm({
      email: registro.email,
      jerarquia: registro.jerarquia,
      puede_ver_dashboard: registro.puede_ver_dashboard,
      puede_administrar_permisos: registro.puede_administrar_permisos,
      puede_eliminar_registros: registro.puede_eliminar_registros,
      puede_ser_medico_responsable: registro.puede_ser_medico_responsable,
      puede_ser_operario_inta: registro.puede_ser_operario_inta,
      activo: registro.activo,
    });
  };

  const handleDelete = async (registro) => {
    const confirmed = window.confirm(`Eliminar el permiso para ${registro.email}?`);
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${API_URL}${registro.id}/`, { headers });
      toast.success('Permiso eliminado correctamente');
      if (editingId === registro.id) {
        resetForm();
      }
      loadRegistros();
    } catch (deleteError) {
      console.error(deleteError);
      toast.error('No se pudo eliminar el permiso');
    }
  };

  return (
    <section className="container page-shell">
      <div className="page-header">
        <div>
          <span className="section-kicker">Administración</span>
          <h2 className="page-title">Permisos por email</h2>
          <p className="page-subtitle">
            Controla que emails pueden ver el dashboard y quienes pueden administrar esta sección.
          </p>
        </div>
        <img src="/logo-inta.png" alt="Logo INTA" className="page-header-logo" />
      </div>

      <div className="row g-4">
        <div className="col-lg-4">
          <div className="surface-card h-100">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div>
                <h3 className="form-title mb-2">{editingId ? 'Editar permiso' : 'Nuevo permiso'}</h3>
                <p className="page-subtitle mb-0">Cada email define jerarquía y accesos habilitados.</p>
              </div>
              {editingId && (
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
                  Cancelar
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Jerarquía</label>
                <select
                  name="jerarquia"
                  className="form-select"
                  value={form.jerarquia}
                  onChange={handleChange}
                >
                  <option value="admin">Administrador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="tecnico">Técnico</option>
                </select>
              </div>

              <div className="form-check mb-2">
                <input
                  id="puede_ver_dashboard"
                  name="puede_ver_dashboard"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.puede_ver_dashboard}
                  onChange={handleChange}
                />
                <label htmlFor="puede_ver_dashboard" className="form-check-label">
                  Puede ver dashboard
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  id="puede_administrar_permisos"
                  name="puede_administrar_permisos"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.puede_administrar_permisos}
                  onChange={handleChange}
                />
                <label htmlFor="puede_administrar_permisos" className="form-check-label">
                  Puede administrar permisos
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  id="puede_eliminar_registros"
                  name="puede_eliminar_registros"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.puede_eliminar_registros}
                  onChange={handleChange}
                />
                <label htmlFor="puede_eliminar_registros" className="form-check-label">
                  Puede eliminar casos y muestras
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  id="puede_ser_medico_responsable"
                  name="puede_ser_medico_responsable"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.puede_ser_medico_responsable}
                  onChange={handleChange}
                />
                <label htmlFor="puede_ser_medico_responsable" className="form-check-label">
                  Puede aparecer como medico responsable
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  id="puede_ser_operario_inta"
                  name="puede_ser_operario_inta"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.puede_ser_operario_inta}
                  onChange={handleChange}
                />
                <label htmlFor="puede_ser_operario_inta" className="form-check-label">
                  Puede aparecer como operario INTA
                </label>
              </div>

              <div className="form-check mb-4">
                <input
                  id="activo"
                  name="activo"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.activo}
                  onChange={handleChange}
                />
                <label htmlFor="activo" className="form-check-label">
                  Registro activo
                </label>
              </div>

              <button type="submit" className="btn btn-brand w-100" disabled={saving}>
                {saving ? 'Guardando...' : editingId ? 'Actualizar permiso' : 'Crear permiso'}
              </button>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="surface-card h-100">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div>
                <h3 className="form-title mb-2">Emails autorizados</h3>
                <p className="page-subtitle mb-0">Listado actual de accesos administrados desde base de datos.</p>
              </div>
              <span className="protocol-chip">{registros.length} registros</span>
            </div>

            {loading ? (
              <div className="dashboard-empty">Cargando permisos...</div>
            ) : error ? (
              <div className="alert subtle-alert mb-0">{error}</div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Jerarquía</th>
                      <th>Dashboard</th>
                      <th>Permisos</th>
                      <th>Eliminar</th>
                      <th>Médico</th>
                      <th>Operario</th>
                      <th>Activo</th>
                      <th className="text-end">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registros.map((registro) => (
                      <tr key={registro.id}>
                        <td>{registro.email}</td>
                        <td>{registro.jerarquia}</td>
                        <td>{registro.puede_ver_dashboard ? 'Si' : 'No'}</td>
                        <td>{registro.puede_administrar_permisos ? 'Si' : 'No'}</td>
                        <td>{registro.puede_eliminar_registros ? 'Si' : 'No'}</td>
                        <td>{registro.puede_ser_medico_responsable ? 'Si' : 'No'}</td>
                        <td>{registro.puede_ser_operario_inta ? 'Si' : 'No'}</td>
                        <td>{registro.activo ? 'Si' : 'No'}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-brand btn-sm"
                              onClick={() => handleEdit(registro)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(registro)}
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PermisosPage;
